import { useEffect, useState } from 'react'

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { IoHandLeftOutline } from 'react-icons/io5'
import {
  CreateAudiostreamInput,
  UpdateAudiostream,
  UpdateAudiostreamInput,
} from 'types/graphql'

import { RWGqlError } from '@cedarjs/forms'
import { toast } from '@cedarjs/web/toast'

import { CommonArrayItem } from 'src/common/types'
import Button from 'src/components/Custom/Button/Button'
import YesNoConfirmationModal from 'src/pages/HeroLineupPage/HeroLineup/YesNoConfirmationModal'

import AudiostreamItem from './AudiostreamItem.tsx'
import ConfigureAudiostreamModal from './ConfigureAudiostreamModal.tsx'

export type Audiostream = UpdateAudiostream['updateAudiostream']

export interface Audiostreams {
  channels: Array<Audiostream>
}

type AudiostreamProps = {
  data: Audiostreams
  loading: boolean
  stationId: string
  streamScheduleOptions: CommonArrayItem[]
  createAudiostream: (input: CreateAudiostreamInput) => Promise<void>
  error?: RWGqlError
  onModalClose: () => void
  updateAudiostream: (
    id: string,
    input: UpdateAudiostreamInput,
    notify?: boolean
  ) => Promise<void>
  deleteAudiostream: (id: string) => Promise<void>
}

const sortByWeight = (channels: Audiostreams['channels']) =>
  [...channels].sort((a, b) => (a.weight ?? 0) - (b.weight ?? 0))

const Audiostream = ({
  data,
  loading,
  stationId,
  streamScheduleOptions,
  createAudiostream,
  updateAudiostream,
  deleteAudiostream,
  error,
  onModalClose,
}: AudiostreamProps) => {
  const [showModal, setShowModal] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [deletingAudiostream, setDeletingAudiostream] =
    useState<Audiostream>(null)
  const [editingItem, setEditingItem] = useState<Audiostream>(null)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const [sortedItems, setSortedItems] = useState(() =>
    sortByWeight(data.channels)
  )
  const [savingOrder, setSavingOrder] = useState(false)

  // Re-sync from the server only when no reorder is in flight, so partial
  // cache updates from individual mutations don't make the list jump around.
  useEffect(() => {
    if (!savingOrder) setSortedItems(sortByWeight(data.channels))
  }, [data.channels, savingOrder])

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    if (savingOrder || !over || active.id === over.id) return

    const oldIndex = sortedItems.findIndex((item) => item.id === active.id)
    const newIndex = sortedItems.findIndex((item) => item.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const reordered = arrayMove(sortedItems, oldIndex, newIndex).map(
      (item, index) => ({ ...item, weight: index })
    )
    const previousWeights = new Map(
      sortedItems.map((item) => [item.id, item.weight])
    )
    const changed = reordered.filter(
      (item) => previousWeights.get(item.id) !== item.weight
    )

    setSortedItems(reordered)
    setSavingOrder(true)

    try {
      const results = await Promise.allSettled(
        changed.map((item) =>
          updateAudiostream(item.id, { weight: item.weight }, false)
        )
      )
      const hasFailure = results.some((result) => result.status === 'rejected')
      if (hasFailure) {
        toast.error('Failed to save the new order')
      }
    } catch {
      toast.error('Failed to save the new order')
    } finally {
      setSavingOrder(false)
    }
  }

  const handleItemClick = (item: Audiostream) => {
    setEditingItem(item)
    setShowModal(true)
  }

  const handleModalClose = () => {
    onModalClose()
    setEditingItem(null)
    setShowModal(false)
  }

  const handleDeleteClick = async (item: Audiostream) => {
    setDeletingAudiostream(item)
    setShowConfirmModal(true)
  }

  const handleDeleteAudiostreamConfirm = async (id: string) => {
    try {
      await deleteAudiostream(id)
      setDeletingAudiostream(null)
      setShowConfirmModal(false)
      if (editingItem?.id === id) handleModalClose()
    } catch {
      setShowConfirmModal(false)
      if (!editingItem) onModalClose()
    }
  }

  return (
    <div className="w-full space-y-2">
      <div className="my-4 flex items-center justify-end space-x-2">
        <div className="flex flex-1 flex-row items-center">
          <IoHandLeftOutline className="mr-2" />
          <p className="text-sm font-normal text-gray-900">
            Drag and drop to reorder.
          </p>
        </div>
        <div className="flex flex-col">
          <Button title="Add audio stream" onClick={() => setShowModal(true)} />
        </div>
      </div>
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        <SortableContext
          items={sortedItems}
          strategy={verticalListSortingStrategy}
        >
          {sortedItems.map((item) => (
            <AudiostreamItem
              item={item}
              key={item.id}
              loading={loading}
              onClick={() => {
                handleItemClick(item)
              }}
              onDelete={() => handleDeleteClick(item)}
            />
          ))}
        </SortableContext>
      </DndContext>
      {showModal && (
        <ConfigureAudiostreamModal
          editingItem={editingItem}
          loading={loading}
          error={error}
          stationId={stationId}
          streamScheduleOptions={streamScheduleOptions}
          showModal={showModal}
          handleModalClose={handleModalClose}
          onSave={async (inputData, id) => {
            if (id) {
              await updateAudiostream(id, inputData, true)
            } else {
              await createAudiostream(inputData as CreateAudiostreamInput)
            }
          }}
          onDelete={() => handleDeleteClick(editingItem)}
        />
      )}
      <YesNoConfirmationModal
        content={`Are you sure you wish to permanently delete audio stream "${deletingAudiostream?.name}"?`}
        show={showConfirmModal}
        setShowModal={setShowConfirmModal}
        onConfirm={() =>
          handleDeleteAudiostreamConfirm(deletingAudiostream?.id)
        }
        onCancel={() => setShowConfirmModal(false)}
      />
    </div>
  )
}

export default Audiostream
